import * as React from 'react'
import { createPortal } from 'react-dom'
import type { GroupModel } from '@/app/models/groupModel'
import {
  DriverGroupAccess,
  DriverGroupAccessError,
  GroupAccessKind,
  useMutationSetDriverGroupAccess,
} from '@/lib/api/company/driverGroupAccess'
import { FaIcon, faPencil } from '@/lib/fa'
import Checkbox from '@/modules/drivers/shared/Checkbox'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

const NAMESPACE: Record<GroupAccessKind, string> = { drivers: 'driverAccess', vehicles: 'vehicleAccess' }
const KNOWN_ERRORS = ['GROUPS_REQUIRED', 'USER_CANNOT_ALTER_ACCESS', 'USER_NOT_RESTRICTABLE', 'NOT_AUTHORIZED']

type Props = {
  kind: GroupAccessKind
  userId: number
  isGlobalAdmin: boolean
  companyId: string
  access: DriverGroupAccess | undefined
  groups: Pick<GroupModel, 'id' | 'name'>[]
  /** Sam kierowca: serwer pomija grupy i daje tylko przypisanego kierowcę (CanReadDriverData). */
  driverOnly?: { driverName: string | null; hasDriver: boolean }
}

/** Dostęp osoby do kierowców albo pojazdów: wszystkie albo tylko wybrane grupy. */
export const GroupAccessCell: React.FC<Props> = ({ kind, userId, isGlobalAdmin, companyId, access, groups, driverOnly }) => {
  const { t } = useLocalize()
  const k = (key: string) => `usersModule.${NAMESPACE[kind]}.${key}`
  const [open, setOpen] = React.useState(false)
  const [draft, setDraft] = React.useState<number[] | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const anchorRef = React.useRef<HTMLButtonElement>(null)
  const popoverRef = React.useRef<HTMLDivElement>(null)
  const mutation = useMutationSetDriverGroupAccess(companyId)

  const current = (kind === 'vehicles' ? access?.vehicleUsers : access?.users)?.[String(userId)] ?? null
  const editorGroups = (kind === 'vehicles' ? access?.vehicleEditorGroupIds : access?.editorGroupIds) ?? null
  // Lista grup przychodzi zawężona do grup edytującego - brak nazwy nie musi znaczyć usuniętej grupy.
  const groupName = (id: number) =>
    groups.find((g) => g.id === id)?.name ??
    t(k(editorGroups && !editorGroups.includes(id) ? 'hiddenGroup' : 'deletedGroup'))
  // Bez eskalacji: ograniczony edytujący zmienia tylko kogoś, kto widzi nie więcej niż on.
  const editable =
    !driverOnly &&
    !!access?.canManage &&
    !isGlobalAdmin &&
    (editorGroups === null || (current !== null && current.every((id) => editorGroups.includes(id))))
  const choices = editorGroups === null ? groups : groups.filter((g) => editorGroups.includes(g.id))

  React.useEffect(() => {
    if (!open) return undefined
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (!popoverRef.current?.contains(target) && !anchorRef.current?.contains(target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const startEdit = () => {
    setDraft(current)
    setError(null)
    setOpen(true)
  }

  const toggleGroup = (id: number, checked: boolean) => {
    const base = draft ?? []
    setDraft(checked ? [...base, id] : base.filter((g) => g !== id))
  }

  const save = async () => {
    setError(null)
    try {
      await mutation.mutateAsync({ userId, groupIds: draft, kind })
      setOpen(false)
    } catch (err) {
      const code = err instanceof DriverGroupAccessError ? err.code : ''
      setError(t(k(`errors.${KNOWN_ERRORS.includes(code) ? code : 'default'}`)))
    }
  }

  const label = driverOnly ? (
    <span className="tw-text-sm tw-text-content-base tw-opacity-70" title={t(k('driverOnlyHint'))}>
      {kind === 'vehicles'
        ? t(k('driverOnly'))
        : driverOnly.hasDriver
          ? `${t(k('driverOnly'))}${driverOnly.driverName ? ` (${driverOnly.driverName})` : ''}`
          : t(k('noDriver'))}
    </span>
  ) : isGlobalAdmin || current === null ? (
      <span className="tw-text-sm tw-text-content-base tw-opacity-70">{t(k('all'))}</span>
    ) : (
      <div className="tw-inline-flex tw-flex-wrap tw-gap-1">
        {current.map((id) => (
          <span key={id} className="badge badge-sm badge-outline">
            {groupName(id)}
          </span>
        ))}
      </div>
    )

  const rect = anchorRef.current?.getBoundingClientRect()
  const restricted = draft !== null

  return (
    <div className="tw-inline-flex tw-items-center tw-gap-2" onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
      {label}
      {editable && (
        <button
          ref={anchorRef}
          type="button"
          title={t(k('edit'))}
          aria-label={t(k('edit'))}
          onClick={() => (open ? setOpen(false) : startEdit())}
          className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full tw-text-primary hover:tw-bg-primary/10"
        >
          <FaIcon icon={faPencil} className="tw-h-3 tw-w-3" />
        </button>
      )}
      {open &&
        rect &&
        createPortal(
          <div
            ref={popoverRef}
            role="dialog"
            style={{ top: rect.bottom + 4, left: Math.max(16, rect.right - 300) }}
            className="tw-fixed tw-z-[9999] tw-w-[300px] tw-rounded-md tw-border tw-border-base-300 tw-bg-content-primary tw-shadow-xl"
          >
            <div className="tw-flex tw-flex-col tw-gap-2 tw-border-b tw-border-base-300 tw-p-3 tw-text-sm tw-text-content-base">
              <label className={`tw-flex tw-items-center tw-gap-2 ${editorGroups !== null ? 'tw-opacity-50' : 'tw-cursor-pointer'}`}>
                <input
                  type="radio"
                  name={`${kind}-access-${userId}`}
                  checked={!restricted}
                  disabled={editorGroups !== null}
                  onChange={() => setDraft(null)}
                />
                {t(k('everyone'))}
              </label>
              <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2">
                <input
                  type="radio"
                  name={`${kind}-access-${userId}`}
                  checked={restricted}
                  onChange={() => setDraft(current ?? [])}
                />
                {t(k('onlyGroups'))}
              </label>
            </div>
            {restricted && (
              <div className="tw-max-h-64 tw-overflow-y-auto tw-p-2">
                {choices.length === 0 ? (
                  <p className="tw-mb-0 tw-p-2 tw-text-xs tw-text-content-base tw-opacity-70">{t(k('noGroups'))}</p>
                ) : (
                  choices.map((group) => (
                    <label
                      key={group.id}
                      className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded tw-px-2 tw-py-1.5 tw-text-sm tw-text-content-base hover:tw-bg-base-200"
                    >
                      <Checkbox
                        checked={draft.includes(group.id)}
                        onChange={(checked) => toggleGroup(group.id, checked)}
                      />
                      {group.name}
                    </label>
                  ))
                )}
                <p className="tw-mb-0 tw-px-2 tw-pt-1 tw-text-xs tw-text-content-base tw-opacity-60">{t(k('hint'))}</p>
              </div>
            )}
            {error && (
              <p role="alert" className="tw-mb-0 tw-border-t tw-border-base-300 tw-bg-error/10 tw-p-3 tw-text-sm tw-text-error">
                {error}
              </p>
            )}
            <div className="tw-flex tw-justify-end tw-gap-2 tw-border-t tw-border-base-300 tw-p-3">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="tw-rounded-md tw-border tw-border-base-300 tw-px-4 tw-py-1.5 tw-text-sm tw-text-content-base hover:tw-bg-base-200"
              >
                {t('common.close')}
              </button>
              <button
                type="button"
                onClick={save}
                disabled={mutation.isPending || (restricted && draft.length === 0)}
                className="tw-rounded-md tw-bg-primary tw-px-4 tw-py-1.5 tw-text-sm tw-text-white hover:tw-bg-primary/90 disabled:tw-opacity-50"
              >
                {mutation.isPending ? t('usersModule.saving') : t('common.save')}
              </button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
}
